import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { AccountEditScreenProps } from "./AccountEditNavigation";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { AHSeparator, AHText, Box, EmployeeAvatar, ListItem } from "arkh";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { AccountEditConnectEmployeeScreenQuery } from "./__generated__/AccountEditConnectEmployeeScreenQuery.graphql";
import { SearchInput } from "../Products/SearchInput";

import { FlatList, Platform } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { SafeAreaProvider } from "react-native-safe-area-context";

import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";

function InnerAccountEditConnectEmployeeScreen(
  props: {
    fetchKey: string;
  } & AccountEditScreenProps<"AccountEditConnectEmployeeScreen">
) {
  const intl_tokens = useIntlTokens();
  const data = useLazyLoadQuery<AccountEditConnectEmployeeScreenQuery>(
    graphql`
      query AccountEditConnectEmployeeScreenQuery($id: ID!, $storeId: ID) {
        store(id: $storeId) {
          storeUser(id: $id) @required(action: THROW) {
            id
            employee {
              id
            }
            user {
              email
            }
          }
          employees {
            edges {
              node {
                id
                name
                storeUser {
                  id
                  user {
                    email
                  }
                }
                ...Avatar_employee
              }
            }
          }
        }
      }
    `,
    { id: props.route.params.id, storeId: props.route.params.storeId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [linkEmployee] = useMutation(graphql`
    mutation AccountEditConnectEmployeeScreenLinkEmployeeMutation(
      $id: ID!
      $employeeId: ID!
      $storeId: ID
    ) {
      store(id: $storeId) {
        storeUser(id: $id) {
          linkEmployee(employeeId: $employeeId) {
            id
            employee {
              id
            }
          }
        }
      }
    }
  `);

  const listData = useMemo(() => {
    return (
      data.store.employees?.edges
        .map((edge) => edge.node)
        .filter((employee) =>
          employee.name?.toLocaleLowerCase().includes(query.toLocaleLowerCase())
        )
        .sort((a, b) => (a.storeUser ? 1 : 0) - (b.storeUser ? 1 : 0)) ?? []
    );
  }, [data.store.employees?.edges, query]);

  const onPress = useCallback(
    (employeeId: string) => {
      linkEmployee({
        variables: {
          id: props.route.params.id,
          employeeId,
          storeId: props.route.params.storeId,
        },
        onCompleted: () => {
          props.navigation.goBack();
        },
      });
    },
    [
      linkEmployee,
      props.navigation,
      props.route.params.id,
      props.route.params.storeId,
    ]
  );

  type ListItemData = (typeof listData)[number];
  const renderItem = useCallback(
    ({ item }: { item: ListItemData }) => {
      return (
        <Box style={{ opacity: item.storeUser ? 0.5 : 1 }}>
          <ListItem
            disabled={!!item.storeUser}
            onPress={() => {
              onPress(item.id);
            }}
            left={<EmployeeAvatar employee={item} size="large" />}
            right={item.storeUser ? null : <ListItem.NavArrow />}
            footer={
              item.storeUser?.user?.email ? (
                <AHText>{item.storeUser?.user?.email}</AHText>
              ) : null
            }
          >
            <ListItem.Title>{item.name}</ListItem.Title>
          </ListItem>
        </Box>
      );
    },
    [onPress]
  );

  const getNavigationHeaderOptions = useNavigationHeaderOptions();
  useLayoutEffect(() => {
    props.navigation.setOptions(
      getNavigationHeaderOptions({
        type: "CLOSE",
        title: "",
        headerTitle: () => (
          <Box style={{ alignItems: "center" }}>
            <AHText type="title-small" spaceBottom="xxsmall">
              {intl_tokens.ConnectEmployee}
            </AHText>
            <AHText type="info-small" color="text-secondary">
              {data.store.storeUser?.user?.email}
            </AHText>
          </Box>
        ),
        onClose: () => {
          props.navigation.goBack();
        },
      })
    );
  }, [
    props.navigation,
    getNavigationHeaderOptions,
    intl_tokens.ConnectEmployee,
    data.store.storeUser,
  ]);

  return (
    <>
      <Box color="surface-primary">
        <SearchInput
          hideSeparator
          showCancel
          query={query}
          isSearching={isSearching}
          onChange={(e) => setQuery(e.nativeEvent.text)}
          onFocus={() => setIsSearching(true)}
          onBlur={() => {
            setQuery("");
            setIsSearching(false);
          }}
        />
      </Box>
      <AHSeparator />
      {Platform.OS === "web" ? (
        <FlatList
          keyboardShouldPersistTaps="handled"
          data={listData}
          renderItem={renderItem}
          ItemSeparatorComponent={AHSeparator}
          keyExtractor={(item) => item.id}
        />
      ) : (
        <FlashList
          keyboardShouldPersistTaps="handled"
          data={listData}
          renderItem={renderItem}
          ItemSeparatorComponent={AHSeparator}
          keyExtractor={(item) => item.id}
          renderScrollComponent={KeyboardAwareFlashListScrollComponent}
        />
      )}
    </>
  );
}

export function AccountEditConnectEmployeeScreen(
  props: AccountEditScreenProps<"AccountEditConnectEmployeeScreen">
) {
  return (
    <SafeAreaProvider>
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => {
          return (
            <InnerAccountEditConnectEmployeeScreen fetchKey={fetchKey} {...props} />
          );
        }}
      </LazyQueryNetworkBoundrary>
    </SafeAreaProvider>
  );
}
